import React from 'react';
import ReactMarkdown from 'react-markdown';
import { stripMarkdown } from '../utils/api';

import {
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  MinusCircle,
  Target,
  User,
  Warning
} from '@phosphor-icons/react';
/**
 * ExportPreviewRenderer - Schema-driven preview renderer for agent exports
 * 
 * Supports section types:
 * - heading: Title/subtitle text
 * - key_value: Label/value rows
 * - bullets: Bulleted list
 * - table: Data table with columns
 * - scorecard: 0-100 score with rating band
 * - divider: Visual separator
 * - richtext: Markdown content
 * 
 * Template syntax:
 * - {{fieldName}} resolves from data context
 * - path: "field" pulls from data object
 * - Missing values show "—"
 */

// ═══════════════════════════════════════════════════════════════════════════════
// VALUE RESOLVER
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Resolve a template value like {{meeting_title}} or a direct path
 */
const resolveValue = (template, data) => {
  if (!template) return '—';
  if (typeof template !== 'string') return template;
  
  // Handle {{handlebars}} syntax
  const handlebarsMatch = template.match(/^\{\{(.+?)\}\}$/);
  if (handlebarsMatch) {
    const path = handlebarsMatch[1].trim();
    return getNestedValue(data, path) ?? '—';
  }
  
  // Handle mixed template strings like "Meeting: {{title}}"
  const resolved = template.replace(/\{\{(.+?)\}\}/g, (match, path) => {
    const value = getNestedValue(data, path.trim());
    return value !== null && value !== undefined ? value : '—';
  });
  
  return resolved;
};

/**
 * Get nested value from object using dot notation
 * Supports: "field", "nested.field", "array[0].field"
 */
const getNestedValue = (obj, path) => {
  if (!obj || !path) return null;
  
  const parts = path.split('.');
  let current = obj;
  
  for (const part of parts) {
    if (current === null || current === undefined) return null;
    
    // Handle array index: items[0]
    const arrayMatch = part.match(/^(\w+)\[(\d+)\]$/);
    if (arrayMatch) {
      current = current[arrayMatch[1]]?.[parseInt(arrayMatch[2])];
    } else {
      current = current[part];
    }
  }
  
  return current;
};

/**
 * Get array value from data, ensuring it's always an array
 */
const getArrayValue = (data, path) => {
  const value = getNestedValue(data, path);
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    // Try to split string into array
    return value.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
  }
  return [];
};

/**
 * Build computed context (e.g., attendees_csv from attendees array)
 */
const buildComputedContext = (data) => {
  const computed = { ...data };
  
  // attendees_csv
  if (Array.isArray(data?.attendees)) {
    computed.attendees_csv = data.attendees.join(', ');
  }
  
  // participants_csv
  if (Array.isArray(data?.participants)) {
    computed.participants_csv = data.participants.join(', ');
  }
  
  return computed;
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION RENDERERS
// ═══════════════════════════════════════════════════════════════════════════════

const HeadingSection = ({ section, data }) => {
  const text = resolveValue(section.text, data);
  const level = section.level || 1;
  
  if (level === 1) {
    return (
      <h2 className="text-xl font-bold text-white mb-2">
        {text}
      </h2>
    );
  }
  
  return (
    <h3 className="text-lg font-semibold text-white mb-2">
      {text}
    </h3>
  );
};

const KeyValueSection = ({ section, data }) => {
  const items = section.items || [];
  
  return (
    <div className="space-y-1.5">
      {items.map((item, idx) => {
        const value = resolveValue(item.value, data);
        return (
          <div key={idx} className="flex items-start gap-2">
            <span className="text-sm text-gray-400 min-w-[100px]">{item.label}:</span>
            <span className="text-sm text-white">{value}</span>
          </div>
        );
      })}
    </div>
  );
};

const BulletsSection = ({ section, data }) => {
  const items = getArrayValue(data, section.path);
  
  if (items.length === 0) {
    return (
      <div className="mb-4">
        {section.title && (
          <h4 className="text-sm font-medium text-white mb-2">{section.title}</h4>
        )}
        <p className="text-sm text-gray-500 italic">No items</p>
      </div>
    );
  }
  
  return (
    <div className="mb-4">
      {section.title && (
        <h4 className="text-sm font-medium text-white mb-2">{section.title}</h4>
      )}
      <ul className="space-y-1.5 pl-4">
        {items.map((item, idx) => (
          <li key={idx} className="text-sm text-gray-300 list-disc list-outside">
            {typeof item === 'object' ? stripMarkdown(item.text || item.content || JSON.stringify(item)) : stripMarkdown(item)}
          </li>
        ))}
      </ul>
    </div>
  );
};

const TableSection = ({ section, data }) => {
  const rows = getArrayValue(data, section.path);
  const columns = section.columns || [];
  
  if (rows.length === 0) {
    return (
      <div className="mb-4">
        {section.title && (
          <h4 className="text-sm font-medium text-white mb-2">{section.title}</h4>
        )}
        <p className="text-sm text-gray-500 italic">No items</p>
      </div>
    );
  }
  
  return (
    <div className="mb-4">
      {section.title && (
        <h4 className="text-sm font-medium text-white mb-2">{section.title}</h4>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgba(255,255,255,0.1)]">
              {columns.map((col, idx) => (
                <th key={idx} className="text-left py-2 px-2 text-gray-400 font-medium">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b border-[rgba(255,255,255,0.05)]">
                {columns.map((col, colIdx) => (
                  <td key={colIdx} className="py-2 px-2 text-gray-300">
                    {row[col.field] || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ScorecardSection = ({ section, data }) => {
  const score = getNestedValue(data, section.path);
  const band = section.band_path ? getNestedValue(data, section.band_path) : null;
  
  // Determine color based on score
  const getScoreColor = (score) => {
    if (score === null || score === undefined) return 'gray';
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    if (score >= 40) return 'orange';
    return 'red';
  };
  
  const color = getScoreColor(score);
  const colorClasses = {
    green: 'from-green-500 to-emerald-600 text-green-400',
    yellow: 'from-yellow-500 to-amber-600 text-yellow-400',
    orange: 'from-orange-500 to-red-500 text-orange-400',
    red: 'from-red-500 to-rose-600 text-red-400',
    gray: 'from-gray-500 to-gray-600 text-gray-400'
  };
  
  const getBandLabel = (score, band) => {
    if (band) return band;
    if (score === null || score === undefined) return 'N/A';
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Very Good';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Satisfactory';
    if (score >= 50) return 'Needs Improvement';
    return 'Needs Work';
  };
  
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)]">
      <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${colorClasses[color].split(' ').slice(0, 2).join(' ')} flex items-center justify-center`}>
        <span className="text-2xl font-bold text-white">
          {score !== null && score !== undefined ? score : '—'}
        </span>
      </div>
      <div>
        <p className="text-sm text-gray-400">{section.label || 'Score'}</p>
        <p className={`text-lg font-semibold ${colorClasses[color].split(' ').pop()}`}>
          {getBandLabel(score, band)}
        </p>
      </div>
    </div>
  );
};

const DividerSection = () => (
  <div className="border-t border-[rgba(255,255,255,0.1)] my-4" />
);

const RichtextSection = ({ section, data }) => {
  const content = section.path ? getNestedValue(data, section.path) : section.content;
  
  if (!content) {
    return (
      <div className="mb-4">
        {section.title && (
          <h4 className="text-sm font-medium text-white mb-2">{section.title}</h4>
        )}
        <p className="text-sm text-gray-500 italic">No content</p>
      </div>
    );
  }
  
  return (
    <div className="mb-4">
      {section.title && (
        <h4 className="text-sm font-medium text-white mb-2">{section.title}</h4>
      )}
      <div className="text-sm text-gray-300 prose prose-sm prose-invert max-w-none">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// GENERIC FALLBACK PREVIEW
// ═══════════════════════════════════════════════════════════════════════════════

const GenericPreview = ({ agentName, outputText, outputStructured }) => {
  // Truncate text to ~800 chars
  const truncatedText = outputText?.substring(0, 800) || '';
  const isTruncated = outputText?.length > 800;
  
  // Extract top-level keys from structured output
  const structuredKeys = outputStructured ? Object.keys(outputStructured).filter(
    k => !['_id', 'id', 'session_id', 'agent_id', 'created_at', 'updated_at'].includes(k)
  ) : [];
  
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-white flex items-center gap-2">
        <FileText size={20} className="text-[#6366F1]" />
        {agentName || 'Agent'} Output
      </h2>
      
      {/* Summary text */}
      {truncatedText && (
        <div>
          <h4 className="text-sm font-medium text-white mb-2">Summary</h4>
          <p className="text-sm text-gray-300 whitespace-pre-wrap">
            {truncatedText}
            {isTruncated && <span className="text-gray-500">...</span>}
          </p>
        </div>
      )}
      
      {/* Structured data preview */}
      {structuredKeys.length > 0 && (
        <>
          <DividerSection />
          <div>
            <h4 className="text-sm font-medium text-white mb-2">Data Fields</h4>
            <div className="space-y-1.5">
              {structuredKeys.slice(0, 10).map(key => {
                const value = outputStructured[key];
                const displayValue = Array.isArray(value) 
                  ? `${value.length} items`
                  : typeof value === 'object'
                    ? 'Object'
                    : String(value).substring(0, 100);
                
                return (
                  <div key={key} className="flex items-start gap-2">
                    <span className="text-sm text-gray-400 min-w-[120px] capitalize">
                      {key.replace(/_/g, ' ')}:
                    </span>
                    <span className="text-sm text-white">{displayValue}</span>
                  </div>
                );
              })}
              {structuredKeys.length > 10 && (
                <p className="text-xs text-gray-500">+{structuredKeys.length - 10} more fields</p>
              )}
            </div>
          </div>
        </>
      )}
      
      {/* No content fallback */}
      {!truncatedText && structuredKeys.length === 0 && (
        <div className="p-4 rounded-lg bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)] text-center">
          <p className="text-sm text-gray-500">No preview data available</p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN RENDERER
// ═══════════════════════════════════════════════════════════════════════════════

const ExportPreviewRenderer = ({ 
  schema,           // Export preview schema from template
  data,             // Output data (output_structured_json)
  outputText,       // Plain text output
  agentName,        // Agent name for generic fallback
  className = ''
}) => {
  // If no schema, render generic preview
  if (!schema || !schema.sections) {
    return (
      <div className={`export-preview ${className}`}>
        <GenericPreview 
          agentName={agentName}
          outputText={outputText}
          outputStructured={data}
        />
      </div>
    );
  }
  
  // Build computed context
  const context = buildComputedContext(data || {});
  
  // Render schema-driven preview
  return (
    <div className={`export-preview space-y-4 ${className}`}>
      {schema.sections.map((section, idx) => {
        switch (section.type) {
          case 'heading':
            return <HeadingSection key={idx} section={section} data={context} />;
          case 'key_value':
            return <KeyValueSection key={idx} section={section} data={context} />;
          case 'bullets':
            return <BulletsSection key={idx} section={section} data={context} />;
          case 'table':
            return <TableSection key={idx} section={section} data={context} />;
          case 'scorecard':
            return <ScorecardSection key={idx} section={section} data={context} />;
          case 'divider':
            return <DividerSection key={idx} />;
          case 'richtext':
            return <RichtextSection key={idx} section={section} data={context} />;
          default:
            console.warn(`Unknown section type: ${section.type}`);
            return null;
        }
      })}
    </div>
  );
};

export default ExportPreviewRenderer;
